// What is currently being filtered out, said in words.
//
// Project Token State has three view buttons and two dropdown filters. The
// buttons change from outline to filled when active, which is easy to miss at
// a glance; the dropdowns give no sign at all of which item was picked, so
// after choosing "On Going" the menu still just reads STATUS.
//
// The result is a table showing a subset with nothing on screen saying so —
// the worst kind of filtered view, because an empty result reads as "there is
// nothing" rather than "you are looking through a slot".
//
// So every non-default choice becomes a chip you can see and remove. Labels
// here, handlers at the call site: this stays a pure description of the state,
// which is the part worth testing.

const STATUS_LABEL = {
  completed: 'Completed',
  ongoing: 'On Going',
  inactive: 'Inactive'
};

const NEXT_PHASE_LABEL = {
  programmed: 'Programmed',
  partial: 'Partially Programmed',
  none: 'Not Programmed'
};

const VIEW_LABEL = {
  showPhantomView: 'Phantoms',
  showPlanAheadView: 'Plan ahead',
  showDestinationView: 'Orders / Stock'
};

/**
 * Every filter currently narrowing the table.
 *
 * @returns [{ key, group, label }] — empty when nothing is filtered, which is
 *          the signal to render no strip at all rather than an empty one.
 */
export function activeFilters({
  showPhantomView = false,
  showPlanAheadView = false,
  showDestinationView = false,
  statusFilter = 'all',
  nextPhaseFilter = 'all',
  selectedPhase = 'all',
  search = ''
} = {}) {
  const out = [];

  const views = { showPhantomView, showPlanAheadView, showDestinationView };
  for (const [key, on] of Object.entries(views)) {
    if (on) out.push({ key, group: 'view', label: VIEW_LABEL[key] });
  }

  if (statusFilter && statusFilter !== 'all') {
    out.push({
      key: 'statusFilter',
      group: 'Status',
      label: STATUS_LABEL[statusFilter] || statusFilter
    });
  }

  if (nextPhaseFilter && nextPhaseFilter !== 'all') {
    out.push({
      key: 'nextPhaseFilter',
      group: 'Next phase',
      label: NEXT_PHASE_LABEL[nextPhaseFilter] || nextPhaseFilter
    });
  }

  // The phase tabs already show their own selection in bold, so this is the
  // one filter that was never invisible — included anyway so "clear all"
  // really does clear everything.
  if (selectedPhase && selectedPhase !== 'all') {
    out.push({
      key: 'selectedPhase',
      group: 'Phase',
      label: String(selectedPhase).replaceAll('_', ' ')
    });
  }

  const q = String(search || '').trim();
  if (q) out.push({ key: 'search', group: 'Search', label: `"${q}"` });

  return out;
}

/** The state that clears everything, for a single setter-driven reset. */
export const CLEARED = {
  showPhantomView: false,
  showPlanAheadView: false,
  showDestinationView: false,
  statusFilter: 'all',
  nextPhaseFilter: 'all',
  selectedPhase: 'all',
  search: ''
};
